import { REGION, regionAt, positionCloseAt } from "./hitRegions";
import { createTapDetector } from "./tapDetector";
import { createPaneDrag } from "./paneDrag";
import { createTimeAxisDrag } from "./axisDrags";

const MOUSE = "mouse";

/**
 * What a press does depending on where it started: a drag of the pane or
 * of the time axis, a double click (double tap) on the time axis, a click (tap) on the
 * close button of a position, which shows as pressed while held, or on a
 * drawing, which selects it; a tap elsewhere removes the selection. On the
 * price pane a drawing under the press takes the drag.
 *
 * @param {object} view - see createInteraction
 */
export const createPressActions = (view) => {
  const { timeScale, overlay, positions, drawings, invalidate } = view;
  const taps = createTapDetector();

  // As in TradingView: back to the default zoom (prices always autoscale, nothing to reset)
  const doubleTapActions = {
    [REGION.TIME_AXIS]: () => timeScale.resetView(),
  };

  return {
    /**
     * @param {{ x: number, y: number }} point - CSS px of the chart
     * @param {boolean} isTouch
     * @returns {{ region: string, positionId: string|number|null, drawingPress?: object|null }} what the press
     *   started on; drawingPress: drawings.resolvePress
     */
    pressTarget: (point, isTouch) => {
      const positionId = positionCloseAt(point, positions.closeButtons, isTouch);

      if (positionId === null) {
        const region = regionAt(point, overlay.layout);
        const drawingPress =
          region === REGION.PANE ? drawings.resolvePress(point, overlay.layout.pane, isTouch) : null;

        return { region, positionId, drawingPress };
      }

      positions.setHighlight({ id: positionId, isPressed: true });

      return { region: REGION.POSITION_CLOSE, positionId };
    },

    /** @param {{ positionId: string|number|null }} press - finished or cancelled */
    release: ({ positionId }) => {
      if (positionId !== null) positions.setHighlight(null);
    },

    /**
     * @param {{ start: object, region: string, drawingPress?: object|null }} press
     * @param {PointerEvent} event - the pointerdown
     * @returns {{ move: Function, end: Function } | null}
     */
    createDrag: ({ start, region, drawingPress }, event) => {
      // The price axis is only a wheel / pinch zone for time: dragging it does nothing
      if (region === REGION.TIME_AXIS) return createTimeAxisDrag(view, start);
      if (region !== REGION.PANE) return null;

      const isTouch = event.pointerType !== MOUSE;
      const drawingGesture =
        drawingPress && drawings.createGesture(drawingPress, start, overlay.layout.pane, isTouch);

      return drawingGesture || createPaneDrag(view, { point: start, time: event.timeStamp, isTouch });
    },

    /**
     * A press released without moving. The close button closes the
     * position when the pointer is still on it.
     *
     * @param {{ start: object, region: string, positionId: string|number|null, drawingPress?: object|null }} press
     * @param {PointerEvent} event - the pointerup
     * @param {{ x: number, y: number }} point - where it was released, CSS px
     */
    handleTap: (press, event, point) => {
      if (press.region === REGION.POSITION_CLOSE) {
        const isTouch = event.pointerType !== MOUSE;

        if (positionCloseAt(point, positions.closeButtons, isTouch) === press.positionId) {
          positions.close(press.positionId);
        }

        return;
      }

      if (press.region === REGION.PANE) drawings.select(press.drawingPress?.id ?? null);

      const isDoubleTap = taps.isDoubleTap({
        ...press.start,
        time: event.timeStamp,
        region: press.region,
      });
      const action = isDoubleTap ? doubleTapActions[press.region] : null;

      if (!action) return;

      action();
      invalidate();
    },
  };
};
